import { Controller } from '@hotwired/stimulus';
import { GoogleCharts } from 'google-charts';

export default class extends Controller {
  static targets = ['pie', 'column', 'bar', 'radar'];

  static values = {
    radius: Number,
    locale: String,
    range: String,
  };

  connect() {
    GoogleCharts.load(this.drawCharts.bind(this));
    GoogleCharts.load('upcoming', {
      packages: ['vegachart'],
    });
  }

  isCondensed() {
    return this.radiusValue <= 80;
  }

  targetsVisible() {
    return (
      (!this.hasPieTarget || this.pieTarget.offsetParent !== null) &&
      (!this.hasColumnTarget || this.columnTarget.offsetParent !== null) &&
      (!this.hasBarTarget || this.barTarget.offsetParent !== null) &&
      (!this.hasRadarTarget || this.radarTarget.offsetParent !== null)
    );
  }

  drawCharts() {
    // Target elements are placed on a tab which may not be selected yet.
    // Wait until all targets are visible
    const checkExist = setInterval(() => {
      if (this.targetsVisible()) {
        if (this.hasPieTarget) this.drawPieChart();

        if (this.hasColumnTarget) this.drawColumnChart();

        if (this.hasBarTarget) this.drawBarChart();

        if (this.hasRadarTarget) this.drawRadarChart();

        clearInterval(checkExist);
      }
    }, 100);
  }

  drawPieChart() {
    const data = JSON.parse(this.pieTarget.dataset.values);
    data.unshift(['Element', 'Kraft']);
    const colors = JSON.parse(this.pieTarget.dataset.colors);

    const pieData = GoogleCharts.api.visualization.arrayToDataTable(data);

    const pieOptions = {
      colors: colors,
      chartArea: {
        width: '100%',
        height: '100%',
      },
      legend: 'none',
      tooltip: {
        text: 'value',
      },
      backgroundColor: 'transparent',
      pieSliceText: 'label',
    };

    const pieChart = new GoogleCharts.api.visualization.PieChart(
      this.pieTarget,
    );
    pieChart.draw(pieData, pieOptions);
  }

  drawColumnChart() {
    const data = JSON.parse(this.columnTarget.dataset.values);
    const colors = JSON.parse(this.columnTarget.dataset.colors);

    const colData = new GoogleCharts.api.visualization.DataTable();
    colData.addColumn('string', '');
    colData.addColumn('number', '');
    colData.addColumn({ type: 'string', role: 'style' });
    colData.addColumn({ type: 'string', role: 'annotation' });

    colData.addRows(data.length);
    for (let i = 0; i < data.length; i++) {
      colData.setValue(i, 0, data[i][0]);
      colData.setValue(i, 1, data[i][1] / 100);
      colData.setValue(i, 2, colors[i]);
      colData.setValue(i, 3, data[i][1].toString() + ' %');
    }

    const view = new GoogleCharts.api.visualization.DataView(colData);

    const colOptions = {
      backgroundColor: 'transparent',
      legend: { position: 'none' },
      hAxis: {
        slantedText: true,
        slantedTextAngle: 45,
        maxTextLines: 2,
        textStyle: {
          color: this.onPrint ? 'black' : this.themeColor('--color-text', 'black'),
        },
      },
      vAxis: {
        maxTextLines: 2,
        format: '#%',
        ticks: [
          {
            v: 0.05,
            f: this.localeValue == 'en' ? 'Undersupplied' : 'Unterversorgt',
          },
          {
            v: 0.15,
            f: this.localeValue == 'en' ? 'Oversupplied' : 'Überschuss',
          },
          { v: 0, f: '0%' },
        ],
        gridlines: {
          color: this.onPrint ? '#cccccc' : this.themeColor('--color-border', '#cccccc'),
        },
        baselineColor: this.onPrint ? 'black' : this.themeColor('--color-text', 'black'),
      },
      annotations: {
        alwaysOutside: true,
        highContrast: false,
        textStyle: {
          color: this.onPrint ? 'black' : this.themeColor('--color-text', 'black'),
        },
      },
    };

    const colChart = new GoogleCharts.api.visualization.ColumnChart(
      this.columnTarget,
    );
    colChart.draw(view, colOptions);
  }

  getValueAt(column, dataTable, row) {
    return dataTable.getFormattedValue(row, column) + '%';
  }

  // Add custom legend for bar chart with compare data
  addBarChartLegend() {
    // Remove existing legend if any
    const existingLegend = this.barTarget.querySelector('.custom-legend');
    if (existingLegend) existingLegend.remove();

    // Create legend container
    const legend = document.createElement('div');
    legend.className = 'custom-legend';
    legend.style.cssText =
      'display: flex; justify-content: center; gap: 24px; margin-top: 8px; font-size: 11px;';

    // Current legend item (solid) - first
    const currentItem = document.createElement('div');
    currentItem.style.cssText = 'display: flex; align-items: center; gap: 6px;';
    const currentBox = document.createElement('div');
    currentBox.style.cssText =
      'width: 20px; height: 14px; background: linear-gradient(90deg, #4a9f4a, #e67e22, #f39c12); border: 1px solid gray;';
    const currentLabel = document.createElement('span');
    currentLabel.style.color = this.onPrint ? '#555' : this.themeColor('--color-text-muted', 'lightgray');
    const rangeText = this.hasRangeValue ? ` (${this.rangeValue})` : '';
    currentLabel.textContent =
      this.localeValue === 'en' ? `Current${rangeText}` : `Aktuell${rangeText}`;
    currentItem.appendChild(currentBox);
    currentItem.appendChild(currentLabel);

    // Birth legend item (solid with white stripes) - second
    const birthItem = document.createElement('div');
    birthItem.style.cssText = 'display: flex; align-items: center; gap: 6px;';
    const birthBox = document.createElement('div');
    birthBox.style.cssText =
      'width: 20px; height: 14px; background: repeating-linear-gradient(45deg, #888, #888 2px, #fff 2px, #fff 4px); border: 1px solid gray;';
    const birthLabel = document.createElement('span');
    birthLabel.style.color = this.onPrint ? '#555' : this.themeColor('--color-text-muted', 'lightgray');
    birthLabel.textContent =
      this.localeValue === 'en' ? 'natal (striped)' : 'Geburt (gestreift)';
    birthItem.appendChild(birthBox);
    birthItem.appendChild(birthLabel);

    legend.appendChild(currentItem);
    legend.appendChild(birthItem);
    this.barTarget.appendChild(legend);
  }

  drawBarChart() {
    const data = JSON.parse(this.barTarget.dataset.values);
    const colors = JSON.parse(this.barTarget.dataset.colors);
    const compareDataRaw = this.barTarget.dataset.compareValues;
    const compareData = compareDataRaw ? JSON.parse(compareDataRaw) : null;

    const barData = new GoogleCharts.api.visualization.DataTable();
    barData.addColumn('string', '');

    if (compareData) {
      // With compare data: show current (solid) first and birth (striped) second
      barData.addColumn('number', 'Aktuell');
      barData.addColumn({ type: 'string', role: 'style' });
      barData.addColumn({ type: 'boolean', role: 'certainty' }); // true = solid fill
      barData.addColumn({ type: 'string', role: 'annotation' });
      barData.addColumn('number', 'Geburt');
      barData.addColumn({ type: 'string', role: 'style' });
      barData.addColumn({ type: 'boolean', role: 'certainty' }); // false = striped pattern

      barData.addRows(data.length);
      for (let i = 0; i < data.length; i++) {
        barData.setValue(i, 0, data[i][0]);
        // Current data - solid element color (certainty: true)
        barData.setValue(i, 1, data[i][1]);
        barData.setValue(i, 2, colors[i]);
        barData.setValue(i, 3, true); // true = solid fill
        barData.setValue(i, 4, data[i][1].toString() + '%');
        // Birth data - solid element color with white stripes (certainty: false, no annotation)
        barData.setValue(i, 5, compareData[i]);
        barData.setValue(i, 6, colors[i]);
        barData.setValue(i, 7, false); // false = striped pattern
      }

      // Sort by current value descending
      barData.sort({ column: 1, desc: true });

      const barOptions = {
        chartArea: {
          top: 20,
          right: 40,
          bottom: 60,
          left: this.element.clientWidth * 0.5,
        },
        backgroundColor: 'transparent',
        legend: { position: 'none' },
        enableInteractivity: true,
        vAxis: {
          gridLines: { count: 10 },
          textStyle: {
            color: this.onPrint ? 'black' : this.themeColor('--color-text', 'lightgray'),
          },
        },
        hAxis: {
          gridLines: { count: 5 },
          ticks: [0, 20, 40, 60, 80, 100],
          format: '#',
          viewWindow: {
            min: 0,
            max: 100,
          },
        },
        annotations: {
          alwaysOutside: true,
          highContrast: true,
          stem: {
            color: 'transparent',
          },
          textStyle: {
            fontSize: 10,
            color: this.onPrint ? 'black' : this.themeColor('--color-text', 'white'),
            auraColor: 'none',
          },
        },
        bar: { groupWidth: '80%' },
      };

      const barChart = new GoogleCharts.api.visualization.BarChart(
        this.barTarget,
      );

      // Listen for chart ready event to fix annotation colors
      GoogleCharts.api.visualization.events.addListener(
        barChart,
        'ready',
        () => {
          // Override annotation text colors to white (or black for print)
          const textColor = this.onPrint ? 'black' : this.themeColor('--color-text', 'white');
          const svg = this.barTarget.querySelector('svg');
          if (svg) {
            // Find all text elements that contain percentage values (annotations)
            const textElements = svg.querySelectorAll('text');
            textElements.forEach((text) => {
              if (text.textContent && text.textContent.includes('%')) {
                text.setAttribute('fill', textColor);
              }
            });
          }
          // Add custom legend below chart
          this.addBarChartLegend();
        },
      );

      barChart.draw(barData, barOptions);
    } else {
      // Original single bar chart without compare data
      barData.addColumn('number', '');
      barData.addColumn({ type: 'string', role: 'style' });

      barData.addRows(data.length);
      for (let i = 0; i < data.length; i++) {
        barData.setValue(i, 0, data[i][0]);
        barData.setValue(i, 1, data[i][1]);
        barData.setValue(i, 2, colors[i]);
      }
      barData.sort({ column: 1, desc: true });

      const view = new GoogleCharts.api.visualization.DataView(barData);
      view.setColumns([
        0,
        1,
        {
          calc: this.getValueAt.bind(undefined, 1),
          sourceColumn: 1,
          type: 'string',
          role: 'annotation',
        },
        2,
      ]);

      const barOptions = {
        chartArea: {
          top: 0,
          right: 40,
          bottom: 0,
          left: this.element.clientWidth * 0.6,
        },
        backgroundColor: 'transparent',
        legend: { position: 'none' },
        enableInteractivity: false,
        vAxis: {
          gridLines: { count: 10 },
          textStyle: {
            color: this.onPrint ? 'black' : this.themeColor('--color-text', 'lightgray'),
          },
        },
        hAxis: {
          gridLines: { count: 5 },
          ticks: [0, 20, 40, 60, 80, 100],
          format: '#',
          viewWindow: {
            min: 0,
            max: 100,
          },
        },
      };

      const barChart = new GoogleCharts.api.visualization.BarChart(
        this.barTarget,
      );
      barChart.draw(view, barOptions);
    }
  }

  drawRadarChart() {
    const data = JSON.parse(this.radarTarget.dataset.values);
    const compareDataRaw = this.radarTarget.dataset.compareValues;
    const compareData = compareDataRaw ? JSON.parse(compareDataRaw) : null;

    // Colors: Birth (yellow) and Current (violet)
    const birthColor = '#C9B95C';
    const currentColor = '#8B5CF6';
    // Use birth color when no comparison, otherwise use current color for main data
    const mainColor = compareData ? currentColor : birthColor;

    const dataTable = new GoogleCharts.api.visualization.DataTable();
    dataTable.addColumn({ type: 'string', id: 'key' });
    dataTable.addColumn({ type: 'number', id: 'value' });
    dataTable.addColumn({ type: 'string', id: 'pattern' });
    dataTable.addColumn({ type: 'string', id: 'color' });
    dataTable.addRows(data);

    // Build compare data inline for Vega spec if available
    const compareDataInline = compareData
      ? compareData.map((item) => ({ key: item[0], value: item[1] }))
      : null;

    // Add overflow visible to container to prevent legend clipping
    if (compareDataInline) {
      this.radarTarget.style.overflow = 'visible';
    }

    const radarOptions = {
      vega: {
        width: this.element.clientWidth,
        height: compareDataInline
          ? this.element.clientHeight + 40
          : this.element.clientHeight,
        padding: compareDataInline
          ? { bottom: 40, top: 0, left: 0, right: 0 }
          : 0,
        autosize: { type: 'none', contains: 'content' },
        signals: [{ name: 'radius', update: this.radiusValue }],
        data: [
          {
            name: 'table',
            source: 'datatable',
          },
          ...(compareDataInline
            ? [
                {
                  name: 'compareTable',
                  values: compareDataInline,
                },
              ]
            : []),
        ],
        scales: [
          {
            name: 'angular',
            type: 'point',
            range: { signal: '[-PI, PI]' },
            padding: 0.5,
            domain: { data: 'table', field: 'key' },
          },
          {
            name: 'radial',
            type: 'linear',
            range: { signal: '[0, radius]' },
            zero: true,
            nice: false,
            domain: [0, 1],
          },
        ],
        encode: {
          enter: {
            x: { signal: 'width/2' },
            y: { signal: 'height/2 + 20' },
          },
        },
        marks: [
          // Compare polygon (birth data) - drawn first (behind)
          ...(compareDataInline
            ? [
                {
                  type: 'group',
                  name: 'compare-patterns',
                  zindex: 0,
                  from: {
                    data: 'compareTable',
                  },
                  marks: [
                    {
                      type: 'line',
                      name: 'compare-pattern-line',
                      from: { data: 'compareTable' },
                      encode: {
                        enter: {
                          interpolate: { value: 'linear-closed' },
                          x: {
                            signal:
                              "scale('radial', datum.value) * cos(scale('angular', datum.key))",
                          },
                          y: {
                            signal:
                              "scale('radial', datum.value) * sin(scale('angular', datum.key))",
                          },
                          stroke: { value: birthColor },
                          strokeWidth: { value: 1.5 },
                          fill: { value: birthColor },
                          fillOpacity: { value: 0.5 },
                        },
                      },
                    },
                  ],
                },
              ]
            : []),
          // Main polygon (current data) - drawn on top
          {
            type: 'group',
            name: 'patterns',
            zindex: 1,
            from: {
              data: 'table',
            },
            marks: [
              {
                type: 'line',
                name: 'pattern-line',
                from: { data: 'table' },
                encode: {
                  enter: {
                    interpolate: { value: 'linear-closed' },
                    x: {
                      signal:
                        "scale('radial', datum.value) * cos(scale('angular', datum.key))",
                    },
                    y: {
                      signal:
                        "scale('radial', datum.value) * sin(scale('angular', datum.key))",
                    },
                    stroke: { value: mainColor },
                    strokeWidth: { value: 1.5 },
                    fill: { value: mainColor },
                    fillOpacity: { value: 0.4 },
                  },
                },
              },
              this.isCondensed()
                ? null
                : {
                    type: 'text',
                    name: 'value-text',
                    from: { data: 'pattern-line' },
                    encode: {
                      enter: {
                        x: {
                          signal:
                            "datum.x + 14 * cos(scale('angular', datum.datum.key))",
                        },
                        y: {
                          signal:
                            "datum.y + 14 * sin(scale('angular', datum.datum.key))",
                        },
                        text: {
                          signal: "format(datum.datum.value,'.0%')",
                        },
                        opacity: {
                          signal: 'datum.datum.value > 0.01 ? 1 : 0',
                        },
                        align: { value: 'center' },
                        baseline: { value: 'middle' },
                        fontWeight: { value: 'bold' },
                        fill: { value: this.onPrint ? 'black' : this.themeColor('--color-text', 'white') },
                      },
                    },
                  },
            ].filter((item) => item),
          },
          {
            type: 'rule',
            name: 'radial-grid',
            from: { data: 'table' },
            zindex: 0,
            encode: {
              enter: {
                x: { value: 0 },
                y: { value: 0 },
                x2: {
                  signal: "radius * cos(scale('angular', datum.key))",
                },
                y2: {
                  signal: "radius * sin(scale('angular', datum.key))",
                },
                stroke: { value: this.themeColor('--color-border', 'lightgray') },
                strokeWidth: { value: 1 },
              },
            },
          },
          {
            type: 'text',
            name: 'key-label',
            from: { data: 'table' },
            zindex: 1,
            encode: {
              enter: {
                x: {
                  signal: "(radius + 25) * cos(scale('angular', datum.key))",
                },
                y: [
                  {
                    test: "sin(scale('angular', datum.key)) > 0",
                    signal: "(radius + 25) * sin(scale('angular', datum.key))",
                  },
                  {
                    test: "sin(scale('angular', datum.key)) < 0",
                    signal: "(radius + 25) * sin(scale('angular', datum.key))",
                  },
                  {
                    signal: "(radius + 20) * sin(scale('angular', datum.key))",
                  },
                ],
                text: { field: 'key' },
                align: {
                  value: 'center',
                },
                baseline: {
                  value: 'middle',
                },
                fill: this.isCondensed()
                  ? { field: 'color' }
                  : { value: this.onPrint ? '#555' : this.themeColor('--color-text-muted', 'lightgray') },
                fontSize: { value: 12 },
              },
            },
          },
          {
            type: 'text',
            name: 'key-element',
            from: { data: 'table' },
            zindex: 1,
            encode: {
              enter: {
                x: {
                  signal: "(radius + 20) * cos(scale('angular', datum.key))",
                },
                y: [
                  {
                    test: "sin(scale('angular', datum.key)) > 0",
                    signal: "(radius + 50) * sin(scale('angular', datum.key))",
                  },
                  {
                    test: "sin(scale('angular', datum.key)) < 0",
                    signal: "(radius + 50) * sin(scale('angular', datum.key))",
                  },
                  {
                    signal:
                      "20 + (radius + 50) * sin(scale('angular', datum.key))",
                  },
                ],
                text: { field: 'pattern' },
                align: {
                  value: 'center',
                },
                baseline: {
                  value: 'middle',
                },
                fill: { field: 'color' },
                fontSize: { value: 11 },
              },
            },
          },
          {
            type: 'line',
            name: 'twenty-line',
            from: { data: 'table' },
            encode: {
              enter: {
                interpolate: { value: 'linear-closed' },
                x: {
                  signal: "0.2 * radius * cos(scale('angular', datum.key))",
                },
                y: {
                  signal: "0.2 * radius * sin(scale('angular', datum.key))",
                },
                stroke: { value: this.themeColor('--color-border', 'lightgray') },
                strokeWidth: { value: 1 },
              },
            },
          },
          {
            type: 'line',
            name: 'fourty-line',
            from: { data: 'table' },
            encode: {
              enter: {
                interpolate: { value: 'linear-closed' },
                x: {
                  signal: "0.4 * radius * cos(scale('angular', datum.key))",
                },
                y: {
                  signal: "0.4 * radius * sin(scale('angular', datum.key))",
                },
                stroke: { value: this.themeColor('--color-border', 'lightgray') },
                strokeWidth: { value: 1 },
              },
            },
          },
          {
            type: 'line',
            name: 'sixty-line',
            from: { data: 'table' },
            encode: {
              enter: {
                interpolate: { value: 'linear-closed' },
                x: {
                  signal: "0.6 * radius * cos(scale('angular', datum.key))",
                },
                y: {
                  signal: "0.6 * radius * sin(scale('angular', datum.key))",
                },
                stroke: { value: this.themeColor('--color-border', 'lightgray') },
                strokeWidth: { value: 1 },
              },
            },
          },
          {
            type: 'line',
            name: 'eighty-line',
            from: { data: 'table' },
            encode: {
              enter: {
                interpolate: { value: 'linear-closed' },
                x: {
                  signal: "0.8 * radius * cos(scale('angular', datum.key))",
                },
                y: {
                  signal: "0.8 * radius * sin(scale('angular', datum.key))",
                },
                stroke: { value: this.themeColor('--color-border', 'lightgray') },
                strokeWidth: { value: 1 },
              },
            },
          },
          {
            type: 'line',
            name: 'outer-line',
            from: { data: 'radial-grid' },
            encode: {
              enter: {
                interpolate: { value: 'linear-closed' },
                x: { field: 'x2' },
                y: { field: 'y2' },
                stroke: { value: this.themeColor('--color-border', 'lightgray') },
                strokeWidth: { value: 1 },
              },
            },
          },
          // Legend for birth vs current comparison - centered at bottom
          ...(compareDataInline
            ? [
                // Birth legend - yellow square (left of center)
                {
                  type: 'rect',
                  name: 'birth-legend-rect',
                  encode: {
                    enter: {
                      x: { signal: '-65' },
                      y: { signal: 'radius + 70' },
                      width: { value: 14 },
                      height: { value: 14 },
                      fill: { value: birthColor },
                      fillOpacity: { value: 0.5 },
                      stroke: { value: birthColor },
                      strokeWidth: { value: 1.5 },
                    },
                  },
                },
                {
                  type: 'text',
                  name: 'birth-legend-text',
                  encode: {
                    enter: {
                      x: { signal: '-45' },
                      y: { signal: 'radius + 77' },
                      text: {
                        value: this.localeValue === 'en' ? 'natal' : 'Geburt',
                      },
                      fill: { value: this.onPrint ? '#555' : this.themeColor('--color-text-muted', 'lightgray') },
                      fontSize: { value: 11 },
                      baseline: { value: 'middle' },
                    },
                  },
                },
                // Current legend - violet square (right of center)
                {
                  type: 'rect',
                  name: 'current-legend-rect',
                  encode: {
                    enter: {
                      x: { signal: '15' },
                      y: { signal: 'radius + 70' },
                      width: { value: 14 },
                      height: { value: 14 },
                      fill: { value: currentColor },
                      fillOpacity: { value: 0.4 },
                      stroke: { value: currentColor },
                      strokeWidth: { value: 1.5 },
                    },
                  },
                },
                {
                  type: 'text',
                  name: 'current-legend-text',
                  encode: {
                    enter: {
                      x: { signal: '35' },
                      y: { signal: 'radius + 77' },
                      text: {
                        value: this.hasRangeValue
                          ? this.localeValue === 'en'
                            ? `Current (${this.rangeValue})`
                            : `Aktuell (${this.rangeValue})`
                          : this.localeValue === 'en'
                            ? 'Current'
                            : 'Aktuell',
                      },
                      fill: { value: this.onPrint ? '#555' : this.themeColor('--color-text-muted', 'lightgray') },
                      fontSize: { value: 11 },
                      baseline: { value: 'middle' },
                    },
                  },
                },
              ]
            : []),
        ],
      },
    };

    const radarChart = new GoogleCharts.api.visualization.VegaChart(
      this.radarTarget,
    );
    radarChart.draw(dataTable, radarOptions);
  }

  get onPrint() {
    return document
      .getElementsByTagName('body')[0]
      .classList.contains('js-print');
  }

  // Resolve a theme CSS var (defined in theme.scss) to a hex/rgb value.
  themeColor(varName, fallback) {
    const v = getComputedStyle(document.documentElement)
      .getPropertyValue(varName)
      .trim();
    return v || fallback;
  }
}
